import { useAtom, useSetAtom } from "jotai";
import { getStores } from "features/Stocks/api";
import {
  conductDocumentsRequest,
  createDocItemsRequest,
  createDocumentRequest,
  deleteDocItemsRequest,
  deleteDocumentsRequest,
  duplicateDocumentRequest,
  getDocItemsRequest,
  getDocumentsRequest,
  getItemLeftAmountRequest,
  unConductDocumentsRequest,
  updateDocItemsRequest,
  updateDocumentRequest,
} from "features/Documents/api/requests";
import moment from "moment/moment";
import { getProducts } from "features/Products/api";
import { getStorePriceList } from "shared-apis/api";
import { getMeasureUnits } from "features/ProductProperties/api";
import { getPrintTemplates } from "features/PrintTemplates/api";
import { DocumentsData } from "constant-data/documentsData";

import { Option } from "components/Select/Select";

import DaysServices from "utils/days";

import { storesAtom } from "../atoms/storeAtom";
import {
  docItemsAtom,
  docItemsToDeleteAtom,
  documentLoadingAtom,
  documentsAtom,
  documentsFiltersAtom,
  openedDocItemAtom,
  openedDocItemStateAtom,
  openedDocumentAtom,
  openedDocumentStateAtom,
  selectedDocItemAtom,
  selectedDocumentAtom,
  selectedDocumentTypeAtom,
  selectedDocumentTypeTemplatesAtom,
} from "../atoms/documentsAtom";
import { Item, PriceListItem, PrintTemplate, StoreItem } from "../types";
import { TDocItem, TDocument } from "../types/documents";
import {
  modal2Atom,
  setDocumentModalAtom,
  setInfoModalAtom,
  setLoadingModalAtom,
  setModal2CloseAtom,
  setModalCloseAtom,
} from "../atoms/modalAtom/modalAtom";
import { allItemsAtom } from "../atoms/itemAtom";
import { allPriceListsAtom } from "../atoms/priceListAtom";
import { measureUnitsAtom } from "../atoms/measureUnitsAtom";

const UseDocuments = () => {
  const [, setStores] = useAtom(storesAtom);
  const [allItems, setAllItems] = useAtom(allItemsAtom);
  const [allPriceLists, setAllPriceLists] = useAtom(allPriceListsAtom);
  const [measureUnits, setMeasureUnits] = useAtom(measureUnitsAtom);
  const [, setSelectedDocumentTypeTemplates] = useAtom(
    selectedDocumentTypeTemplatesAtom
  );

  const [selectedDocumentType] = useAtom(selectedDocumentTypeAtom);
  const [documentsFilters, setDocumentsFilters] = useAtom(documentsFiltersAtom);
  const [documents, setDocuments] = useAtom(documentsAtom);
  const [selectedDocument, setSelectedDocument] = useAtom(selectedDocumentAtom);
  const [openedDocument, setOpenedDocument] = useAtom(openedDocumentAtom);
  const [openedDocumentState, setOpenedDocumentState] = useAtom(
    openedDocumentStateAtom
  );
  const [docItems, setDocItems] = useAtom(docItemsAtom);
  const [selectedDocItem, setSelectedDocItem] = useAtom(selectedDocItemAtom);
  const [openedDocItem, setOpenedDocItem] = useAtom(openedDocItemAtom);
  const [openedDocItemState, setOpenedDocItemState] = useAtom(
    openedDocItemStateAtom
  );
  const [docItemsToDelete, setDocItemsToDelete] = useAtom(docItemsToDeleteAtom);

  const closeModal = useSetAtom(setModalCloseAtom);
  const closeModal2 = useSetAtom(setModal2CloseAtom);
  const setShowDocumentModal = useSetAtom(setDocumentModalAtom);
  const setInfoModal = useSetAtom(setInfoModalAtom);
  const setLoadingModal = useSetAtom(setLoadingModalAtom);

  const getAndSaveData = async () => {
    const storeData: StoreItem[] | void = await getStores();
    const itemsData: Item[] | void = await getProducts();
    const priceListsData: PriceListItem[] | void = await getStorePriceList();
    const measureUnitsData = await getMeasureUnits();

    if (Array.isArray(storeData)) setStores(storeData);
    if (Array.isArray(itemsData)) setAllItems(itemsData);
    if (Array.isArray(priceListsData)) setAllPriceLists(priceListsData);
    if (Array.isArray(measureUnitsData)) setMeasureUnits(measureUnitsData);
  };

  const getAndSavePrintTemplates = async (docOperationId) => {
    const { data: templatesData } = await getPrintTemplates({
      doc_operation: docOperationId,
    });

    if (Array.isArray(templatesData))
      setSelectedDocumentTypeTemplates(templatesData);
  };

  const getDocuments = async () => {
    const data: TDocument[] | void =
      await getDocumentsRequest(documentsFilters);

    if (Array.isArray(data)) {
      setDocuments(data);
    }
  };

  const getDocItems = async (docid: string) => {
    const data: TDocItem[] | void = await getDocItemsRequest({
      docid,
    });

    if (Array.isArray(data)) {
      setDocItems(
        data.map((i, index) => ({
          ...i,
          index: data.length - index,
        }))
      );
    }
  };

  const getItemLeftAmount = async (
    itemid: string,
    setLoading: (val: boolean) => void
  ) => {
    setLoading(true);
    const data: number | void = await getItemLeftAmountRequest({
      docid: openedDocument.docid,
      itemid,
    });
    setLoading(false);

    if (typeof data === "number") {
      return data;
    }

    return 0;
  };

  const createDocumentAndOpen = async () => {
    const data: TDocument | void = await createDocumentRequest({
      operation_id: selectedDocumentType!.id,
    });

    if (data) {
      setDocumentsFilters({
        dateTo: DaysServices.getDayEnd(new Date(moment().toDate())),
      } as any);
      setSelectedDocument(data);
      setOpenedDocument(data);
      setShowDocumentModal();
    }
  };

  const openDocument = () => {
    setOpenedDocument({ ...selectedDocument });
    setShowDocumentModal();
    getDocItems(selectedDocument.docid).then();
  };

  const deleteDocument = async () => {
    const res: { message: "success" | string } | void =
      await deleteDocumentsRequest({
        docids: [selectedDocument.docid],
      });

    if (res && res.message === "success") {
      setDocuments(documents.filter((i) => i.docid !== selectedDocument.docid));
      setSelectedDocument(null);
      closeModal();
    }
  };

  const conductDocument = async () => {
    if (!selectedDocumentType?.id) return;
    const { shouldCheck2Stores } =
      DocumentsData[selectedDocumentType?.id] || {};

    if (
      !selectedDocument?.storeid ||
      (shouldCheck2Stores && !selectedDocument?.store2id)
    ) {
      return setInfoModal({
        title: shouldCheck2Stores
          ? "Проводить можно только документы с выбранными складами"
          : "Проводить можно только документы с выбранным складом",
      });
    }
    setLoadingModal({});
    const res: { message: "success" | string; errorMessages: string[] } | void =
      await conductDocumentsRequest({
        docids: [selectedDocument.docid],
      });

    if (res && res.message === "success") {
      if (res.errorMessages?.length) {
        setInfoModal({
          title: `Возникли следующие ошибки:\n${res.errorMessages.join("\n")}`,
        });
      } else {
        setDocuments(
          documents.map((i) =>
            i.docid === selectedDocument.docid ? { ...i, ok: 1 } : i
          )
        );
        setSelectedDocument(null);
        closeModal();
      }
    } else {
      closeModal();
    }
  };

  const unConductDocument = async () => {
    const res: { message: "success" | string } | void =
      await unConductDocumentsRequest({
        docids: [selectedDocument.docid],
      });

    if (res && res.message === "success") {
      setDocuments(
        documents.map((i) =>
          i.docid === selectedDocument.docid ? { ...i, ok: 0 } : i
        )
      );
      setSelectedDocument(null);
      closeModal();
    }
  };

  const updateDocument = async (shouldCloseAndConduct?: boolean) => {
    const isConducting =
      shouldCloseAndConduct && Boolean(openedDocument.storeid);
    await createDocItems();
    await deleteDocItems();
    await updateDocItems();

    if (openedDocument.ok || isConducting) {
      setLoadingModal({
        atomToOpen: modal2Atom,
        setAtomClose: setModal2CloseAtom,
      });
    }
    const res: TDocument | void = await updateDocumentRequest({
      docid: openedDocument.docid,
      operation_id: openedDocument.type,

      storeid: openedDocumentState.storeid,
      time: openedDocumentState.time,
      comment: openedDocumentState.comment,

      priceupdated: openedDocumentState.priceupdated ? 1 : 0,
      store2id: openedDocumentState.store2id,

      shouldConduct: isConducting,
    });

    if (!shouldCloseAndConduct) {
      await getDocItems(openedDocument.docid);
    }

    setInfoModal({
      title: "Изменения были успешно сохранены",
      atomToOpen: modal2Atom,
      setAtomClose: setModal2CloseAtom,
    });

    if (res?.docid) {
      setDocuments(
        documents.map((i) =>
          i.docid === selectedDocument.docid ? { ...i, ...res } : i
        )
      );
      setSelectedDocument((prev) => ({
        ...prev,
        ...res,
        number: prev.number,
      }));
      setOpenedDocument(res);
      if (shouldCloseAndConduct) {
        closeDocumentModal();
      }
    }
  };

  const saveDocItem = () => {
    const docItemPayload = {
      item: allItems.find((i) => i.itemid === openedDocItemState.itemid),
      item2: allItems.find((i) => i.itemid === openedDocItemState.item2id),
      measure: measureUnits.find(
        (i) => i.measureid === openedDocItemState.measureid
      ),
      measure2: measureUnits.find(
        (i) => i.measureid === openedDocItemState.measure2id
      ),
      pricelist: allPriceLists.find(
        (i) => i.pricelistid === openedDocItemState.pricelistid
      ),
      price: openedDocItemState.price,
      realamount: openedDocItemState.realamount,
      amount: openedDocItemState.amount || 0,
      difference:
        (Number(openedDocItemState.amount) || 0) -
          (Number(openedDocItemState.realamount) || 0) || 0,
      total: openedDocItemState.total,
      salepercent: openedDocItemState.salepercent,
      frontprice: openedDocItemState.frontprice,
      itemcomment: openedDocItemState.itemcomment,
    };
    if (openedDocItem) {
      // editing existing
      const newDocItem = {
        ...openedDocItem,
        ...docItemPayload,
        isEdited: true,
      };
      setDocItems(
        docItems.map((i) =>
          i.docitemid === newDocItem.docitemid ? newDocItem : i
        )
      );
    } else {
      // creating new
      const newDocItem = {
        ...docItemPayload,
        docid: openedDocument.docid,
        isNew: true,
        docitemid: Math.random(),
        index: docItems.length + 1,
      };
      setDocItems([newDocItem, ...docItems]);
    }

    closeDocItemModal();
  };

  const createManyDocItems = (items: Item[]) => {
    const newDocItems: TDocItem[] = [];

    for (let i = 0; i < items.length; i++) {
      const item = items[i];

      newDocItems.push({
        docid: openedDocument.docid,
        isNew: true,
        docitemid: Math.random(),
        index: docItems.length + 1 + i,
        item: item,
        measure: measureUnits.find((i) => i.measureid === item.measureid),
        price: 0,
        realamount: item.realamount,
        amount: 0,
        difference: -(item.realamount || 0),
        total: 0,
      });
    }

    setDocItems([...newDocItems.reverse(), ...docItems]);
  };

  const removeDocItem = () => {
    if (!selectedDocItem) return;
    setDocItems(
      docItems.filter((i) => i.docitemid !== selectedDocItem.docitemid)
    );
    if (!selectedDocItem.isNew) {
      setDocItemsToDelete([...docItemsToDelete, selectedDocItem]);
    }
    setSelectedDocItem(null);
    closeModal2();
  };

  const createDocItems = async () => {
    let docItemsToCreate = docItems
      .filter((i) => i.isNew)
      .map((i) => ({
        ...i,
        isNew: undefined,
        docitemid: undefined,
        index: undefined,
        itemid: i.item?.itemid,
        item2id: i.item2?.itemid,
        measureid: i.measure?.measureid,
        measure2id: i.measure2?.measureid,
        pricelistid: i.pricelist?.pricelistid,
      }));

    if (!docItemsToCreate.length) return;

    if (openedDocument.type === 8) {
      docItemsToCreate = docItemsToCreate.map((i) => ({
        ...i,
        item2id: undefined,
        measure2id: undefined,
        pricelistid: undefined,
      }));
    }

    const data: { message: "success" | string } | void =
      await createDocItemsRequest({ docitems: docItemsToCreate });

    if (data && data.message === "success") {
      return true;
    }
  };

  const updateDocItems = async () => {
    let docItemsToUpdate = docItems
      .filter((i) => i.isEdited && !i.isNew)
      .map((i) => ({
        ...i,
        isNew: undefined,
        isEdited: undefined,
        index: undefined,
        itemid: i.item?.itemid,
        item2id: i.item2?.itemid,
        measureid: i.measure?.measureid,
        measure2id: i.measure2?.measureid,
        pricelistid: i.pricelist?.pricelistid,
      }));

    if (!docItemsToUpdate.length) return;

    if (openedDocument.type === 8) {
      docItemsToUpdate = docItemsToUpdate.map((i) => ({
        ...i,
        item2id: undefined,
        measure2id: undefined,
        pricelistid: undefined,
      }));
    }

    const data: { message: "success" | string } | void =
      await updateDocItemsRequest({
        docitems: docItemsToUpdate,
      });

    if (data && data.message === "success") {
      return true;
    }
  };

  const deleteDocItems = async () => {
    const docitemids = docItemsToDelete.map((i) => i.docitemid);

    if (!docitemids.length) return;

    const data: { message: "success" | string } | void =
      await deleteDocItemsRequest({
        docitemids,
        docid: docItemsToDelete[0].docid,
      });

    if (data && data.message === "success") {
      return true;
    }
  };

  const duplicateDocument = async (val: Option | undefined) => {
    if (!val) return;

    const data: TDocument | void = await duplicateDocumentRequest({
      docid: openedDocument.docid,
      newDocType: val.value,
    });

    if (data?.docid) {
      setInfoModal({
        title: `Новый документ ${val.label.toLowerCase()} по номеру "${data.docid}" был создан`,
        atomToOpen: modal2Atom,
        setAtomClose: setModal2CloseAtom,
      });
      return true;
    }
  };

  const closeDocumentModal = () => {
    setOpenedDocument(null as any);
    setOpenedDocumentState({} as any);
    setDocItems([]);
    setSelectedDocItem(null);
    setOpenedDocItem(null as any);
    setOpenedDocItemState({} as any);
    closeModal();
  };

  const closeDocItemModal = () => {
    setOpenedDocItem(null as any);
    setOpenedDocItemState({ shouldReplace: true } as any);
    closeModal2();
  };

  return {
    getAndSavePrintTemplates,
    createDocumentAndOpen,
    deleteDocument,
    conductDocument,
    unConductDocument,
    getDocuments,
    getAndSaveData,
    updateDocument,
    closeDocumentModal,
    closeDocItemModal,
    saveDocItem,
    getDocItems,
    openDocument,
    removeDocItem,
    duplicateDocument,
    getItemLeftAmount,
    createManyDocItems,
  };
};

export default UseDocuments;
